import { createSelector } from 'reselect';
import { mapWithDefault } from '../../../utils/dataMapper';

const selectStepTripCancellationTravellerTugo = (state) => state.get('stepTripCancellationTravellerTugo');

const HEALTH_SUB_QUESTION_PREFIXES = [
  'youOrTravellingCompanion',
  'familyMember',
  'businessPartnerOrEmployer',
  'hostAtDestination',
];

const PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES = [
  'legalAdoption',
  'pregnancyDiagnosed',
  'complicationsOfPregnancy',
];

const EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES = [
  'jobTransfer',
  'involuntaryLossOfEmployment',
  'cancellationOfBusinessMeeting',
  'cancellationOfConference',
];

const LEGAL_ISSUES_SUB_QUESTION_PREFIXES = [
  'canadianGovernmentAdvisory',
  'juryDutyCourtProceeding',
  'summonedToService',
  'officialTravelAdvisory',
  'nonIssuanceOfVisa',
];

const CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES = [
  'missedConnection',
  'fourHoursNoConnection',
  'cancellationOfCommonCarrier',
  'tourOperatorCancellation',
  'accidentOnWay',
  'delayOfPrivateVehicle',
];

const OTHER_REASONS_SUB_QUESTION_PREFIXES = [
  'naturalDisaster',
  'unforeseeableEvent',
];

const TOUR_EXPENSE_ITEMS_FIELDS = [
  'NonRefundablePrePaidTourCostsItems',
  'NonRefundablePrePaidTransportationCostsNotPartOfTourItems',
];

const EXPENSE_ITEMS_FIELDS = [
  'NonRefundablePrePaidTravelCostsItems',
  'NonRefundablePrePaidTransportationCostsItems',
  'CommercialAccommodationToResumeTravelItems',
  'ChangeFeesIfCancellingPortionOfTripItems',
  'SingleSupplementItems',
];

// Build selector fields for a set of sub-question prefixes
function buildSubQuestionSelectors(stepState, prefixes) {
  const result = {};
  prefixes.forEach((prefix) => {
    result[`${prefix}SelectedExpenses`] = mapWithDefault(
      stepState,
      () => stepState.getIn(['form', `${prefix}SelectedExpenses`]),
      []
    );
    result[`${prefix}ChangeFeeOption`] = mapWithDefault(
      stepState,
      () => stepState.getIn(['form', `${prefix}ChangeFeeOption`]),
      ''
    );
    EXPENSE_ITEMS_FIELDS.forEach((field) => {
      result[`${prefix}${field}`] = mapWithDefault(
        stepState,
        () => stepState.getIn(['form', `${prefix}${field}`]),
        []
      );
    });
  });
  return result;
}

const makeSelectStepTripCancellationTravellerTugo = () =>
  createSelector(selectStepTripCancellationTravellerTugo, (stepState) => ({
    cancelledBeforeLeaving: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'cancelledBeforeLeaving']),
      ''
    ),
    changedAfterLeaving: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'changedAfterLeaving']),
      ''
    ),
    whatExpensesAreYouClaimingFor: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'whatExpensesAreYouClaimingFor']),
      []
    ),
    descriptionOfLoss: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'descriptionOfLoss']),
      ''
    ),
    amountClaimed: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'amountClaimed']),
      ''
    ),
    healthSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'healthSubQuestions']),
      []
    ),
    pregnancyAdoptionSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'pregnancyAdoptionSubQuestions']),
      []
    ),
    ...buildSubQuestionSelectors(stepState, HEALTH_SUB_QUESTION_PREFIXES),
    ...buildSubQuestionSelectors(stepState, PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES),
    employmentEducationSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'employmentEducationSubQuestions']),
      []
    ),
    ...buildSubQuestionSelectors(stepState, EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES),
    legalIssuesSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'legalIssuesSubQuestions']),
      []
    ),
    ...buildSubQuestionSelectors(stepState, LEGAL_ISSUES_SUB_QUESTION_PREFIXES),
    carrierAccidentWeatherSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'carrierAccidentWeatherSubQuestions']),
      []
    ),
    ...buildSubQuestionSelectors(stepState, CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES),
    otherReasonsSubQuestions: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'otherReasonsSubQuestions']),
      []
    ),
    ...buildSubQuestionSelectors(stepState, OTHER_REASONS_SUB_QUESTION_PREFIXES),
    otherReasonsCfarChecked: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'otherReasonsCfarChecked']),
      false
    ),
    cancelForAnyReasonItems: mapWithDefault(
      stepState,
      () => stepState.getIn(['form', 'cancelForAnyReasonItems']),
      []
    ),
    // Tour-specific expense item selectors for tourOperatorCancellation
    ...TOUR_EXPENSE_ITEMS_FIELDS.reduce((acc, field) => {
      acc[`tourOperatorCancellation${field}`] = mapWithDefault(
        stepState,
        () => stepState.getIn(['form', `tourOperatorCancellation${field}`]),
        []
      );
      return acc;
    }, {}),
  }));

const makeSelectStepTripCancellationTravellerTugoErrors = () =>
  createSelector(selectStepTripCancellationTravellerTugo, (state) => state.get('errors'));

export {
  makeSelectStepTripCancellationTravellerTugo,
  makeSelectStepTripCancellationTravellerTugoErrors,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.TripCancellationTraveller/selectors.js